import { useEffect, useState } from "react";
import { Timer, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { moodPaths } from "@/components/music/MoodTab";

const PRESETS = [10, 15, 25, 45, 60];
export const clock = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

export const useCountdown = (sprint) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!sprint) return undefined; const t = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(t); }, [sprint]);
  return sprint ? sprint.endsAt - now : 0;
};

const Active = ({ m }) => {
  const left = useCountdown(m.sprint);
  const pct = 100 - (left / (m.sprint.minutes * 60000)) * 100;
  return (
    <div className="space-y-3 text-center" data-testid="sprint-active">
      <div className="font-display text-5xl tabular-nums text-[#F3EBDD]" data-testid="sprint-countdown">{clock(left)}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#1A202C]"><div className="h-full rounded-full bg-[#F59E0B] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <p className="text-[11px] text-[#A8A090]">{m.sprint.minutes}-minute sprint{m.sprint.mood ? ` · ${m.sprint.mood} mix` : ""}. Music fades out when time's up.</p>
      <Button size="sm" variant="outline" onClick={m.stopSprint} data-testid="sprint-stop"><Square className="mr-1 h-3 w-3" />End sprint now</Button>
    </div>
  );
};

const Setup = ({ m }) => {
  const [minutes, setMinutes] = useState(25);
  const [music, setMusic] = useState("keep");
  const begin = () => {
    const mood = m.allMoods.includes(music) ? music : null;
    if (mood) m.play(moodPaths(m, mood), null, `${mood} mix`, { mood });
    if (music === "none" && m.playing) m.toggle();
    m.startSprint(minutes, mood);
  };
  return (
    <div className="space-y-3" data-testid="sprint-setup">
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => <button key={p} onClick={() => setMinutes(p)} className={`rounded-full px-2.5 py-0.5 text-[11px] ${minutes === p ? "bg-[#D97706] text-[#0B0D10]" : "border border-[#2D3442] text-[#A8A090] hover:border-[#D97706]/50"}`} data-testid={`sprint-preset-${p}`}>{p} min</button>)}
        <input type="number" min={1} max={180} value={minutes} onChange={(e) => setMinutes(Math.min(180, Math.max(1, Number(e.target.value) || 1)))} className="w-14 rounded border border-[#2D3442] bg-transparent px-1.5 text-[11px] text-[#F3EBDD]" aria-label="Minutes" data-testid="sprint-minutes" />
      </div>
      <Select value={music} onValueChange={setMusic}>
        <SelectTrigger className="h-8 text-xs" data-testid="sprint-music-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="keep">Keep the current music</SelectItem>
          <SelectItem value="none">No music</SelectItem>
          {m.allMoods.map((md) => { const n = moodPaths(m, md).length; return <SelectItem key={md} value={md} disabled={!n} data-testid={`sprint-mood-${md}`}>{md} mix{n ? ` · ${n}` : Object.keys(m.moods).length ? " · no songs yet" : " · sort your library first"}</SelectItem>; })}
        </SelectContent>
      </Select>
      <Button size="sm" className="w-full" onClick={begin} data-testid="sprint-start"><Timer className="mr-1 h-3.5 w-3.5" />Start {minutes}-minute sprint</Button>
    </div>
  );
};

const History = ({ m }) => (m.history?.length ? (
  <ul className="space-y-1 border-t border-[#2D3442] pt-2" data-testid="sprint-history">
    {m.history.slice(0, 5).map((h) => (
      <li key={h.at} className="flex justify-between text-[11px] text-[#A8A090]">
        <span>{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {h.minutes} min{h.mood ? ` · ${h.mood}` : ""}</span>
        <span className="tabular-nums text-[#E8DFD1]">{h.words == null ? "—" : `${h.words.toLocaleString()} words`}</span>
      </li>
    ))}
  </ul>
) : null);

export const SprintTab = ({ m }) => (
  <div className="space-y-3" data-testid="music-sprint">
    {m.result && !m.sprint && (
      <div className="rounded border border-[#10B981]/40 bg-[#10B981]/10 p-3 text-center animate-in zoom-in-95 fade-in duration-300" data-testid="sprint-result">
        <div className="text-[11px] uppercase tracking-widest text-[#34D399]">Sprint complete</div>
        <div className="mt-1 font-display text-2xl text-[#F3EBDD]" data-testid="sprint-result-words">{m.result.words == null ? "Counting…" : `${m.result.words.toLocaleString()} words`}</div>
        {m.result.words != null && <div className="text-[11px] text-[#A8A090]">in {m.result.minutes} {m.result.minutes === 1 ? "minute" : "minutes"}</div>}
        <button onClick={m.clearResult} className="mt-1 text-[10px] text-[#7A7468] hover:text-[#F3EBDD]" data-testid="sprint-result-dismiss">Dismiss</button>
      </div>
    )}
    {m.sprint ? <Active m={m} /> : <Setup m={m} />}
    <History m={m} />
  </div>
);
